/**
 * FH-A11Y batch 24 (2026-10-03): Rivyo "Write a review" and "Ask a question" pop-ups, by keyboard and screen reader.
 * Same look. What changes:
 *   - the pop-up is a named dialog; focus moves into it, Tab stays inside it, Escape closes it, and focus
 *     goes back to the button that opened it;
 *   - the Close button has a name; the "shown publicly like" dropdown has a label;
 *   - the star rating can be reached and set with the keyboard (arrow keys), and reads "1 star" to "5 stars";
 *   - the photo upload ("Browse") can be reached with the keyboard;
 *   - error messages are tied to their fields and read out, and focus moves to the first field with an error.
 * Nothing runs until one of the two Rivyo buttons is used, so there is no cost on page load.
 * Rollback: remove the batch 24 line at the end of snippets/freshdesk.liquid.
 */
(function () {
  'use strict';
  if (window.__fhA11yBatch24) return;
  window.__fhA11yBatch24 = true;

  var OPEN = '.wcRivyoModal.show';
  var opener = null;
  var modal = null;
  var watchModal = null;
  var watchParent = null;
  var lastErrors = '';
  var submitted = false;
  var raf = 0;

  function setAttr(el, n, v) { if (el && el.getAttribute(n) !== v) el.setAttribute(n, v); }
  function say(msg) { if (typeof window.fhA11yAnnounce === 'function') window.fhA11yAnnounce(msg, 200); }

  function addStyles() {
    if (document.getElementById('fh-a11y-batch24-css')) return;
    var st = document.createElement('style');
    st.id = 'fh-a11y-batch24-css';
    st.textContent =
      /* The app hides the star radios and the file input with display:none, which also removes them from the
         keyboard. Keep them invisible but reachable. */
      '.wcRivyoModal .wc_review_rating_contain,.wcRivyoModal .wc_review_form_ctm_image{position:relative}' +
      '.wcRivyoModal .wc_review_rating_contain input[type="radio"],.wcRivyoModal input#review_img_id{' +
      'display:block!important;position:absolute!important;width:1px!important;height:1px!important;margin:0!important;' +
      'padding:0!important;border:0!important;opacity:0!important;overflow:hidden!important;clip-path:inset(50%)!important;' +
      'pointer-events:none!important}' +
      '.wcRivyoModal .wc_review_rating_contain input[type="radio"]:focus-visible+label,' +
      '.wcRivyoModal input#review_img_id:focus-visible+.wc_review_file_upload label,' +
      '.wcRivyoModal .wcRivyoModalClose:focus-visible{outline:2px solid #404040!important;outline-offset:2px!important}';
    document.head.appendChild(st);
  }

  function visible(el) { return !!(el.offsetParent || el.getClientRects().length); }
  function focusables(m) {
    return Array.prototype.filter.call(
      m.querySelectorAll('a[href],button,input,select,textarea,[tabindex]'),
      function (el) { return !el.disabled && el.tabIndex >= 0 && el.type !== 'hidden' && visible(el); }
    );
  }
  function focus(el) { if (el) { try { el.focus({ preventScroll: false }); } catch (e) { el.focus(); } } }

  function decorate(m) {
    var title = m.querySelector('.wcRivyoModal_title h3');
    if (title && !title.id) title.id = 'fh-rivyo-dialog-title';
    setAttr(m, 'role', 'dialog');
    setAttr(m, 'aria-modal', 'true');
    if (title) setAttr(m, 'aria-labelledby', title.id);
    setAttr(m.querySelector('.wcRivyoModalClose'), 'aria-label', 'Close');

    var nameInput = m.querySelector('input[name="name"]');
    setAttr(nameInput, 'aria-label', 'Name');
    setAttr(nameInput, 'autocomplete', 'name');
    setAttr(m.querySelector('input[name="email"]'), 'autocomplete', 'email');
    setAttr(m.querySelector('select[name="initial_name"]'), 'aria-label', 'How your name is shown publicly');

    var stars = m.querySelector('.wc_review_rating_contain');
    if (stars) {
      setAttr(stars, 'role', 'radiogroup');
      setAttr(stars, 'aria-label', 'Rating');
      setAttr(stars, 'aria-required', 'true');
      stars.querySelectorAll('input[type="radio"]').forEach(function (r) {
        setAttr(r, 'aria-label', r.value === '1' ? '1 star' : r.value + ' stars');
      });
    }

    /* Errors: tie each message to its field. */
    var seen = [];
    var messages = [];
    var firstInvalid = null;
    m.querySelectorAll('.wc_error_text').forEach(function (err, i) {
      var text = (err.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return;
      if (!err.id) err.id = 'fh-rivyo-error-' + i + '-' + Date.now().toString(36);
      var field = null;
      if (stars && stars.contains(err)) field = stars;
      else {
        var col = err.closest('.wc_rivyo_form_col');
        field = col && col.querySelector('input:not([type="hidden"]):not([type="file"]),textarea');
      }
      if (!field) return;
      setAttr(field, 'aria-describedby', err.id);
      if (field !== stars) setAttr(field, 'aria-invalid', 'true');
      field.__fh24err = true;
      seen.push(field);
      messages.push(text);
      if (!firstInvalid) firstInvalid = field === stars ? (stars.querySelector('input[type="radio"]:checked') || stars.querySelector('input[type="radio"]')) : field;
    });
    m.querySelectorAll('[aria-describedby^="fh-rivyo-error-"]').forEach(function (field) {
      if (seen.indexOf(field) !== -1) return;
      field.removeAttribute('aria-describedby');
      field.removeAttribute('aria-invalid');
    });

    var key = messages.join('. ');
    if (key !== lastErrors) {
      lastErrors = key;
      if (key) {
        say(messages.length + (messages.length === 1 ? ' error. ' : ' errors. ') + key);
        if (submitted) focus(firstInvalid);
      }
    }
    submitted = false;
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(function () {
      raf = 0;
      if (modal && modal.isConnected) { try { decorate(modal); } catch (e) {} }
    });
  }

  function onKeydown(e) {
    var m = modal;
    if (!m) return;
    if (e.key === 'Escape') {
      var close = m.querySelector('.wcRivyoModalClose');
      if (close) { e.preventDefault(); e.stopPropagation(); close.click(); }
      return;
    }
    if (e.key === 'Tab') {
      var list = focusables(m);
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var a = document.activeElement;
      if (!m.contains(a)) { e.preventDefault(); focus(first); }
      else if (e.shiftKey && a === first) { e.preventDefault(); focus(last); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); focus(first); }
      return;
    }
    /* Stars are laid out 1 to 5 from the left but sit 5 to 1 in the page code, so the browser's own
       arrow-key order would run backwards. */
    var t = e.target;
    if (t && t.type === 'radio' && t.name === 'wc_rating') {
      var step = (e.key === 'ArrowRight' || e.key === 'ArrowUp') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowDown') ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      var current = t.checked ? parseInt(t.value, 10) : (step > 0 ? 0 : 6);
      var next = Math.min(5, Math.max(1, current + step));
      var target = m.querySelector('input[name="wc_rating"][value="' + next + '"]');
      if (target) { if (!target.checked) target.click(); focus(target); }
    }
  }
  function onSubmitIntent(e) {
    var t = e.target;
    if (t && t.closest && t.closest('button[type="submit"]')) submitted = true;
  }
  function onFormSubmit() { submitted = true; }

  function teardown() {
    if (watchModal) { watchModal.disconnect(); watchModal = null; }
    if (watchParent) { watchParent.disconnect(); watchParent = null; }
    document.removeEventListener('keydown', onKeydown, true);
    modal = null;
    lastErrors = '';
    var o = opener;
    opener = null;
    setTimeout(function () {
      var a = document.activeElement;
      if (o && o.isConnected && (!a || a === document.body || !a.isConnected)) focus(o);
    }, 60);
  }

  function setup(m) {
    if (modal === m) return;
    modal = m;
    lastErrors = '';
    submitted = false;
    addStyles();
    decorate(m);
    document.addEventListener('keydown', onKeydown, true);
    m.addEventListener('click', onSubmitIntent, true);
    m.addEventListener('submit', onFormSubmit, true);
    watchModal = new MutationObserver(schedule);
    watchModal.observe(m, { childList: true, subtree: true, characterData: true });
    var parent = m.parentElement;
    if (parent) {
      watchParent = new MutationObserver(function () {
        if (!m.isConnected || !m.classList.contains('show')) teardown();
      });
      watchParent.observe(parent, { childList: true, attributes: true, attributeFilter: ['class'], subtree: true });
    }
    setTimeout(function () {
      if (modal !== m || m.contains(document.activeElement)) return;
      focus(m.querySelector('input[name="name"]') || focusables(m)[0]);
    }, 80);
  }

  function lookForModal(tries) {
    var m = document.querySelector(OPEN);
    if (m) { setup(m); return; }
    if (tries > 0) setTimeout(function () { lookForModal(tries - 1); }, 120);
  }

  /* The only thing that runs before a shopper asks for the form. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('button.wcRivyoFeedback');
    if (!b) return;
    opener = b;
    lookForModal(12);
  }, true);
})();

/**
 * FH-A11Y batch 25 (2026-10-04): gift card balance page (/pages/gift-card-balance, drawn by the gift card app).
 * Same look, apart from a keyboard focus outline on the code field and the "Check now" button. What changes:
 *   - the code field has a name ("Gift card code") instead of only a placeholder;
 *   - "Gift card not found" is read out and tied to the field; a found balance is read out too;
 *   - Enter in the field checks the code, the same as the button.
 * Does nothing on any other page.
 */
(function () {
  'use strict';
  if (window.__fhA11yBatch25) return;
  window.__fhA11yBatch25 = true;
  if (location.pathname.indexOf('gift-card-balance') === -1) return;

  var lastSaid = '';
  var timer = 0;

  function setAttr(el, n, v) { if (el && el.getAttribute(n) !== v) el.setAttribute(n, v); }
  function say(msg) { if (typeof window.fhA11yAnnounce === 'function') window.fhA11yAnnounce(msg, 200); }
  function text(el) { return el ? (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  function addStyles() {
    if (document.getElementById('fh-a11y-batch25-css')) return;
    var st = document.createElement('style');
    st.id = 'fh-a11y-batch25-css';
    st.textContent =
      '.gvl-balance .gvl-form__item-wrapper:has(.gvl-input:focus-visible){outline:2px solid #404040!important;outline-offset:2px!important}' +
      '.gvl-balance .gvl-btn:focus-visible{outline:2px solid #404040!important;outline-offset:2px!important}' +
      '@supports not selector(:has(*)){.gvl-balance .gvl-input:focus-visible{outline:2px solid #404040!important;outline-offset:2px!important}}';
    document.head.appendChild(st);
  }

  function fix() {
    var root = document.querySelector('.gvl-balance');
    if (!root) return;
    addStyles();
    var input = root.querySelector('.gvl-balance-code-input');
    var button = root.querySelector('.gvl-balance-checkout-btn');
    if (input) {
      setAttr(input, 'aria-label', 'Gift card code');
      setAttr(input, 'autocomplete', 'off');
      if (!input.__fh25) {
        input.__fh25 = true;
        input.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' || e.isComposing) return;
          var b = root.querySelector('.gvl-balance-checkout-btn');
          if (!b || b.disabled) return;
          e.preventDefault();
          lastSaid = '';
          b.click();
        });
      }
    }
    if (button && !button.__fh25) {
      button.__fh25 = true;
      button.addEventListener('click', function () { lastSaid = ''; });
    }

    /* While a check is running the old message is still on screen: wait for the new result. */
    if (button && button.classList.contains('gvl-btn--loading')) return;

    var err = root.querySelector('.gvl-error-msg');
    var errText = text(err);
    if (err && errText) {
      if (!err.id) err.id = 'fh-gvl-balance-error';
      setAttr(input, 'aria-invalid', 'true');
      setAttr(input, 'aria-describedby', err.id);
      if (lastSaid !== 'e:' + errText) { lastSaid = 'e:' + errText; say(errText); }
      return;
    }
    if (input && input.getAttribute('aria-describedby') === 'fh-gvl-balance-error') {
      input.removeAttribute('aria-describedby');
      input.removeAttribute('aria-invalid');
    }

    var details = root.querySelector('.gvl-balance__details');
    if (details && !details.classList.contains('gvl-hidden')) {
      var found = text(details.querySelector('.gvl-card-details'));
      if (found && lastSaid !== 'd:' + found) {
        lastSaid = 'd:' + found;
        say(found.length > 240 ? found.slice(0, 240) : found);
      }
    }
  }

  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = 0; try { fix(); } catch (e) {} }, 120);
  }
  function start() {
    fix();
    /* The app draws the form after the page loads and redraws parts of it after each check. */
    var main = document.getElementById('MainContent') || document.body;
    new MutationObserver(schedule).observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
